import React from "react";
import { useTranslation } from "react-i18next";

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  subtitle,
  message, 
  confirmText = "Bestätigen",
  cancelText = "Abbrechen",
  type = "danger", // danger, warning, info
  layout = "default", // default (icon + title centered) or "inline" (icon left, title + subtitle right)
  isLoading = false, // Disables buttons and shows loading state
  disabled = false // Disables confirm button
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return 'fas fa-exclamation-triangle';
      case 'warning':
        return 'fas fa-exclamation-circle';
      case 'info':
        return 'fas fa-info-circle';
      default:
        return 'fas fa-question-circle';
    }
  };

  const getIconColor = () => {
    switch (type) {
      case 'danger':
        return 'text-red-600 dark:text-red-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'info':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-slate-600 dark:text-slate-400';
    }
  };

  const getIconBgColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-100 dark:bg-red-900/30';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/30';
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900/30';
      default:
        return 'bg-slate-100 dark:bg-slate-700';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700';
      default:
        return 'bg-slate-600 hover:bg-slate-700';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      style={{ minHeight: '100vh', minHeight: '100dvh' }}
    >
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700">
        <div className="p-6">
          {layout === "inline" ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 ${getIconBgColor()} rounded-full flex items-center justify-center`}>
                  <i className={`${getIcon()} ${getIconColor()} text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                    {title}
                  </h3>
                  {subtitle && (
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {message && (
                <p className="text-gray-700 dark:text-slate-300 mb-6">
                  {message}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-center gap-4 mb-4">
                <i className={`${getIcon()} ${getIconColor()} text-3xl`}></i>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                  {title}
                </h2>
              </div>
              {message && (
                <div className="text-center py-4 text-slate-700 dark:text-slate-300 text-base leading-relaxed">
                  {message}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 rounded-b-xl">
          <button 
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button 
            className={`flex-1 px-4 py-2 text-sm font-medium text-white ${getButtonColor()} rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={handleConfirm}
            disabled={isLoading || disabled}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {confirmText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
